import type { Thread } from "../types";
import type { ComposerImageAttachment, DraftThreadEnvMode } from "../composerDraftStore";
import { parseStandaloneComposerSlashCommand, type ComposerSlashCommand } from "../composer-logic";
import type { ComposerDisabledReason } from "./chat/composerTurnPlan";

export type { ComposerDisabledReason };
import {
  filterTerminalContextsWithText,
  stripInlineTerminalContextPlaceholders,
  type TerminalContextDraft,
} from "../lib/terminalContext";

export function resolveSendEnvMode(input: {
  requestedEnvMode: DraftThreadEnvMode;
  isGitRepo: boolean;
  gitStatusKnown: boolean;
}): DraftThreadEnvMode {
  // Worktree mode needs a git repo to fork from, and "this directory is not a
  // repo" is the only thing that downgrades a requested worktree to a run in
  // the project directory. It fires only once git status has loaded: an unknown
  // status is not evidence of "no repo", so a requested worktree rides through
  // unchanged until the query resolves and can confirm it.
  //
  // Not knowing a base branch is deliberately NOT a reason to downgrade. The
  // client's `defaultBranch` is the REMOTE default (`origin/HEAD`), which is
  // null on any clone without a remote or whose resolution failed, and the
  // server resolves the repo's own default when it receives a null base branch.
  // Treating that as "this repo has no branch" is how a thread the user started
  // in New worktree mode ended up running in the shared checkout with nothing
  // on the thread to say so (FOR-36238).
  if (input.requestedEnvMode === "worktree" && !input.gitStatusKnown) {
    return "worktree";
  }
  return input.isGitRepo ? input.requestedEnvMode : "local";
}

// A failed worktree bootstrap leaves worktreePath null forever: without this
// check the thread would silently derive "local" and every later send would
// run in the shared project checkout instead of retrying the worktree.
function threadHasPendingWorktreeIntent(
  activeThread:
    | {
        readonly activities?: ReadonlyArray<{ readonly kind: string }>;
      }
    | null
    | undefined,
): boolean {
  return Boolean(
    activeThread?.activities?.some(
      (activity) => activity.kind === "worktree.requested" || activity.kind === "worktree.failed",
    ),
  );
}

function resolveActiveThreadBranch(input: {
  readonly activeThread: { readonly branch: string | null } | null | undefined;
  readonly canOverrideServerThreadEnvMode: boolean;
  readonly pendingServerThreadBranch: string | null | undefined;
  readonly defaultBranch: string | null | undefined;
}): string | null {
  if (input.canOverrideServerThreadEnvMode && input.pendingServerThreadBranch !== undefined) {
    return input.pendingServerThreadBranch;
  }
  return input.activeThread?.branch ?? (input.activeThread ? (input.defaultBranch ?? null) : null);
}

export function deriveThreadEnvModeState(input: {
  readonly activeThread:
    | {
        readonly messages: ReadonlyArray<unknown>;
        readonly worktreePath: string | null;
        readonly branch: string | null;
        readonly activities?: ReadonlyArray<{ readonly kind: string }>;
      }
    | null
    | undefined;
  readonly isServerThread: boolean;
  readonly isLocalDraftThread: boolean;
  readonly draftThreadEnvMode: DraftThreadEnvMode | undefined;
  readonly pendingServerThreadEnvMode: DraftThreadEnvMode | null;
  readonly pendingServerThreadBranch: string | null | undefined;
  readonly defaultBranch: string | null | undefined;
  readonly envLocked: boolean;
  readonly isGitRepo: boolean;
  readonly gitStatusKnown: boolean;
}): {
  readonly activeWorktreePath: string | null;
  readonly derivedEnvMode: DraftThreadEnvMode;
  readonly canOverrideServerThreadEnvMode: boolean;
  readonly envMode: DraftThreadEnvMode;
  readonly activeThreadBranch: string | null;
  readonly sendEnvMode: DraftThreadEnvMode;
} {
  const activeWorktreePath = input.activeThread?.worktreePath ?? null;
  const derivedEnvMode: DraftThreadEnvMode = activeWorktreePath
    ? "worktree"
    : threadHasPendingWorktreeIntent(input.activeThread)
      ? "worktree"
      : input.isServerThread
        ? "local"
        : (input.draftThreadEnvMode ?? "local");
  const canOverrideServerThreadEnvMode = Boolean(
    input.isServerThread &&
    input.activeThread &&
    input.activeThread.messages.length === 0 &&
    input.activeThread.worktreePath === null &&
    !input.envLocked,
  );
  const envMode = canOverrideServerThreadEnvMode
    ? (input.pendingServerThreadEnvMode ?? input.draftThreadEnvMode ?? derivedEnvMode)
    : derivedEnvMode;
  const activeThreadBranch = resolveActiveThreadBranch({
    activeThread: input.activeThread,
    canOverrideServerThreadEnvMode,
    pendingServerThreadBranch: input.pendingServerThreadBranch,
    defaultBranch: input.defaultBranch,
  });
  return {
    activeWorktreePath,
    derivedEnvMode,
    canOverrideServerThreadEnvMode,
    envMode,
    activeThreadBranch,
    sendEnvMode: resolveSendEnvMode({
      requestedEnvMode: envMode,
      isGitRepo: input.isGitRepo,
      gitStatusKnown: input.gitStatusKnown,
    }),
  };
}

export function cloneComposerImageForRetry(
  image: ComposerImageAttachment,
): ComposerImageAttachment {
  if (typeof URL === "undefined" || !image.previewUrl.startsWith("blob:")) {
    return image;
  }
  try {
    return {
      ...image,
      previewUrl: URL.createObjectURL(image.file),
    };
  } catch {
    return image;
  }
}

export function deriveComposerSendState(options: {
  prompt: string;
  imageCount: number;
  fileCount?: number;
  terminalContexts: ReadonlyArray<TerminalContextDraft>;
}): {
  trimmedPrompt: string;
  sendableTerminalContexts: TerminalContextDraft[];
  expiredTerminalContextCount: number;
  hasSendableContent: boolean;
} {
  const trimmedPrompt = stripInlineTerminalContextPlaceholders(options.prompt).trim();
  const sendableTerminalContexts = filterTerminalContextsWithText(options.terminalContexts);
  const expiredTerminalContextCount =
    options.terminalContexts.length - sendableTerminalContexts.length;
  return {
    trimmedPrompt,
    sendableTerminalContexts,
    expiredTerminalContextCount,
    hasSendableContent:
      trimmedPrompt.length > 0 ||
      options.imageCount + (options.fileCount ?? 0) > 0 ||
      sendableTerminalContexts.length > 0,
  };
}

export type ComposerSendPreflightResult =
  | {
      readonly type: "provider-disabled";
      /**
       * The refusal's sentence, or null when the send is held for a verdict
       * still loading: blocked, with nothing to show (forge#3394).
       */
      readonly reason: string | null;
    }
  | {
      readonly type: "advance-pending-user-input";
    }
  | {
      readonly type: "submit-plan-follow-up";
    }
  | {
      readonly type: "apply-slash-command";
      readonly command: Exclude<ComposerSlashCommand, "model">;
    }
  | {
      readonly type: "empty-content";
      readonly expiredTerminalContextCount: number;
    }
  | {
      readonly type: "missing-project";
    }
  | {
      readonly type: "send-turn";
      readonly isFirstMessage: boolean;
      readonly baseBranchForWorktree: string | null;
      readonly shouldCreateWorktree: boolean;
    };

function deriveWorktreeSendPreflight(input: {
  readonly activeThread: { readonly worktreePath: string | null } | null | undefined;
  readonly isFirstMessage: boolean;
  readonly sendEnvMode: DraftThreadEnvMode;
  readonly activeThreadBranch: string | null;
}): Extract<ComposerSendPreflightResult, { type: "send-turn" }> {
  const shouldCreateWorktree = Boolean(
    input.activeThread &&
    input.isFirstMessage &&
    input.sendEnvMode === "worktree" &&
    !input.activeThread.worktreePath,
  );

  // A base branch the client cannot name is sent as null, whether or not git
  // status has resolved: the server resolves the repo's own default branch for
  // a null base, and a repo that genuinely has none fails visibly on the thread
  // rather than quietly running in the project directory.
  return {
    type: "send-turn",
    isFirstMessage: input.isFirstMessage,
    baseBranchForWorktree: shouldCreateWorktree ? input.activeThreadBranch : null,
    shouldCreateWorktree,
  };
}

export function deriveComposerSendPreflight(input: {
  readonly composerDisabledReason: ComposerDisabledReason;
  readonly hasActivePendingProgress: boolean;
  readonly showPlanFollowUpPrompt: boolean;
  readonly hasActiveProposedPlan: boolean;
  readonly trimmedPrompt: string;
  readonly imageCount: number;
  readonly fileCount?: number;
  readonly sendableTerminalContextCount: number;
  readonly expiredTerminalContextCount: number;
  readonly activeProjectExists: boolean;
  readonly activeThread:
    | {
        readonly id?: string;
        readonly messages: ReadonlyArray<unknown>;
        readonly worktreePath: string | null;
      }
    | null
    | undefined;
  readonly isServerThread: boolean;
  readonly sendEnvMode: DraftThreadEnvMode;
  readonly activeThreadBranch: string | null;
}): ComposerSendPreflightResult {
  // `false` is a held send with deliberately no copy (the verdict still
  // loading), so the null check, not truthiness, is what gates this.
  if (input.composerDisabledReason !== null) {
    return { type: "provider-disabled", reason: input.composerDisabledReason || null };
  }

  if (input.hasActivePendingProgress) {
    return { type: "advance-pending-user-input" };
  }

  if (input.showPlanFollowUpPrompt && input.hasActiveProposedPlan) {
    return { type: "submit-plan-follow-up" };
  }

  const standaloneSlashCommand =
    input.imageCount + (input.fileCount ?? 0) === 0 && input.sendableTerminalContextCount === 0
      ? parseStandaloneComposerSlashCommand(input.trimmedPrompt)
      : null;
  if (standaloneSlashCommand) {
    return { type: "apply-slash-command", command: standaloneSlashCommand };
  }

  const hasSendableContent =
    input.trimmedPrompt.length > 0 ||
    input.imageCount + (input.fileCount ?? 0) > 0 ||
    input.sendableTerminalContextCount > 0;
  if (!hasSendableContent) {
    return {
      type: "empty-content",
      expiredTerminalContextCount: input.expiredTerminalContextCount,
    };
  }

  if (!input.activeProjectExists) {
    return { type: "missing-project" };
  }

  const isFirstMessage = !input.isServerThread || input.activeThread?.messages.length === 0;

  return deriveWorktreeSendPreflight({
    activeThread: input.activeThread,
    isFirstMessage,
    sendEnvMode: input.sendEnvMode,
    activeThreadBranch: input.activeThreadBranch,
  });
}

export function buildExpiredTerminalContextToastCopy(
  expiredTerminalContextCount: number,
  variant: "omitted" | "empty",
): { title: string; description: string } {
  const count = Math.max(1, Math.floor(expiredTerminalContextCount));
  const noun = count === 1 ? "Expired terminal context" : "Expired terminal contexts";
  if (variant === "empty") {
    return {
      title: `${noun} won't be sent`,
      description: "Remove it or re-add it to include terminal output.",
    };
  }
  return {
    title: `${noun} omitted from message`,
    description: "Re-add it if you want that terminal output included.",
  };
}

export function threadHasStarted(thread: Thread | null | undefined): boolean {
  return Boolean(
    thread && (thread.latestTurn !== null || thread.messages.length > 0 || thread.session !== null),
  );
}
